import {
  ClickAwayListener,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
} from '@mui/material';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useAutocompleteSuggestions } from '../hooks/useAutocompleteSuggestions';
import TextField, { type TextFieldProps } from '../TextField/TextField';
import { useStyles } from './AddressTextField.styles';
import { DEFAULT_GOOGLE_MAPS_LOGO_SRC, type PlaceResult } from './types';

const puertoRicoPrimaryTypes = ['subpremise', 'geocode'];

export const INCOMPLETE_ADDRESS_ERROR_MESSAGE =
  'That address is incomplete. Please contact us for help.';

type PresetProps = 'value' | 'onChange' | 'onBlur' | 'autoComplete';

export type AddressTextFieldBaseProps = Omit<TextFieldProps, PresetProps> & {
  value: string;
  onValueChange: (value: string) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onInputChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onPlaceSelected?: (place: PlaceResult) => void;
  googleMapsLogoSrc?: string;
};

export default function AddressTextFieldBase({
  value,
  onValueChange,
  onBlur,
  onInputChange,
  onPlaceSelected,
  googleMapsLogoSrc = DEFAULT_GOOGLE_MAPS_LOGO_SRC,
  ...textFieldProps
}: AddressTextFieldBaseProps) {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const { suggestions, resetSession } = useAutocompleteSuggestions(input, {
    includedPrimaryTypes: [
      'street_address',
      'premise',
      ...puertoRicoPrimaryTypes,
    ],
    includedRegionCodes: ['us', 'pr'],
  });
  const placesLib = useMapsLibrary('places');
  const { classes } = useStyles();
  const id = useId();
  const addressListId = `address-list-${id}`;
  const getOptionId = (index: number) => `address-option-${id}-${index}`;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value) {
      setInput(value);
    }
  }, [value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset selection when suggestions change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [suggestions]);

  const handlePlaceSelected = async (
    placePrediction: google.maps.places.PlacePrediction
  ): Promise<void> => {
    if (!placesLib) return;

    const place = new placesLib.Place({ id: placePrediction.placeId });

    await place.fetchFields({
      fields: ['location', 'formattedAddress', 'addressComponents'],
    });

    const getComp = (type: string, useShort = false) => {
      const comp = place.addressComponents?.find((c) => c.types.includes(type));
      return useShort ? comp?.shortText || '' : comp?.longText || '';
    };

    const { formattedAddress, location } = place;
    const zipCode = getComp('postal_code');
    const state = getComp('administrative_area_level_1', true);
    const puertoRicoAddressFallback =
      getComp('premise') ||
      getComp('subpremise') ||
      getComp('neighborhood') ||
      formattedAddress?.split(',')[0]?.trim() ||
      '';

    const streetAddress =
      `${getComp('street_number')} ${getComp('route')}`.trim() ||
      puertoRicoAddressFallback;
    const city =
      getComp('locality') ||
      getComp('sublocality_level_1') ||
      getComp('neighborhood') ||
      formattedAddress?.split(',')[1]?.trim() ||
      '';

    if (
      formattedAddress &&
      zipCode &&
      state &&
      location &&
      streetAddress &&
      city
    ) {
      const placeResult: PlaceResult = {
        formattedAddress,
        location: {
          lat: location.lat(),
          long: location.lng(),
        },
        address: {
          streetAddress,
          city,
          zipCode,
          state,
        },
      };

      setSelectionError(null);
      setInput(placeResult.formattedAddress);
      setShowSuggestions(false);
      onValueChange(placeResult.formattedAddress);
      onPlaceSelected?.(placeResult);
      resetSession();
      return;
    }

    setSelectionError(INCOMPLETE_ADDRESS_ERROR_MESSAGE);
    setShowSuggestions(false);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectionError(null);
    setInput(event.target.value);
    setShowSuggestions(true);
    onValueChange('');
    onInputChange?.(event);
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    textFieldProps.onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setShowSuggestions(true);
      setSelectedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setShowSuggestions(true);
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (event.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    } else if (event.key === 'Enter') {
      const selectedPrediction = suggestions?.[selectedIndex]?.placePrediction;
      if (showSuggestions && selectedPrediction) {
        event.preventDefault();
        void handlePlaceSelected(selectedPrediction);
      }
    }
  };

  const isDropdownOpen = showSuggestions && suggestions.length > 0;

  // Hide the RHF-driven error while focused so retyping to pick a new
  // suggestion after an earlier blur doesn't immediately turn red again.
  // The local selectionError (incomplete address) is unaffected, since it's
  // only ever set right after an explicit dropdown selection.
  const showFieldError = !isFocused && Boolean(textFieldProps.error);
  const hasError = Boolean(selectionError) || showFieldError;

  const getHelperText = (): AddressTextFieldBaseProps['helperText'] => {
    if (selectionError) return selectionError;
    if (showFieldError) return textFieldProps.helperText;
    return undefined;
  };

  return (
    <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
      <div>
        <TextField
          {...textFieldProps}
          ref={inputRef}
          value={input}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoComplete='off'
          role='combobox'
          aria-autocomplete='list'
          aria-expanded={isDropdownOpen}
          aria-haspopup='listbox'
          aria-controls={addressListId}
          aria-activedescendant={
            selectedIndex >= 0 ? getOptionId(selectedIndex) : undefined
          }
          error={hasError}
          helperText={getHelperText()}
        />

        <Popper
          open={isDropdownOpen}
          anchorEl={inputRef.current}
          placement='bottom-start'
          style={{ width: inputRef.current?.offsetWidth, zIndex: 1300 }}
          modifiers={[{ name: 'flip', enabled: false }]}
        >
          <Paper elevation={3} className={classes.autocompleteDropdown}>
            <List id={addressListId} role='listbox'>
              {suggestions.map(({ placePrediction }, index) => {
                if (!placePrediction) return null;

                return (
                  <ListItem
                    key={placePrediction.placeId}
                    role='option'
                    id={getOptionId(index)}
                    aria-selected={selectedIndex === index}
                    disablePadding
                  >
                    <ListItemButton
                      onMouseDown={(event) => {
                        event.preventDefault();
                        void handlePlaceSelected(placePrediction);
                      }}
                      selected={selectedIndex === index}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      <ListItemText
                        primary={placePrediction.text.text}
                        slotProps={{
                          primary: {
                            className: classes.autocompleteSuggestionText,
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
              <ListItem
                key='google-maps-logo'
                className={classes.googleLogoListItem}
              >
                {/* biome-ignore lint/performance/noImgElement: Google attribution requires img */}
                <img
                  data-testid='google-maps-logo'
                  src={googleMapsLogoSrc}
                  alt='Google Maps'
                  className={classes.googleLogo}
                />
              </ListItem>
            </List>
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
